/**
 * Monotone cubic interpolation (Fritsch–Carlson), as an SVG path.
 *
 * The balance line wants to be smooth — a polyline through nine monthly
 * points reads as a chart, and this screen is trying to read as an account.
 * But the obvious way to smooth it (a Catmull-Rom spline, which is what most
 * chart libraries reach for) overshoots: between a flat month and a steep
 * drop it bulges past both, so the curve shows the loge holding more money
 * than it ever held, at a moment it never held it. On a balance chart that
 * isn't a cosmetic liberty, it's a wrong number drawn at full size.
 *
 * Fritsch–Carlson flattens the tangent wherever the data changes direction,
 * which guarantees the curve stays within the values it connects: every local
 * maximum is a real month, and a flat stretch stays flat.
 */

export interface Point {
  x: number;
  y: number;
}

const round = (n: number) => Math.round(n * 100) / 100;

/** Tangents at each point, chosen so the curve can't overshoot its data. */
function tangents(points: Point[]): number[] {
  const n = points.length;
  const dx: number[] = [];
  const slopes: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const h = points[i + 1].x - points[i].x;
    dx.push(h);
    slopes.push(h === 0 ? 0 : (points[i + 1].y - points[i].y) / h);
  }

  const m: number[] = new Array(n);
  m[0] = slopes[0];
  m[n - 1] = slopes[n - 2];
  for (let i = 1; i < n - 1; i++) {
    const a = slopes[i - 1];
    const b = slopes[i];
    // A direction change (or a flat neighbour) pins the tangent to zero, so
    // the point itself is the extreme — this is the no-overshoot rule.
    if (a * b <= 0) {
      m[i] = 0;
    } else {
      const average = (a + b) / 2;
      const limit = 3 * Math.min(Math.abs(a), Math.abs(b));
      m[i] = Math.sign(average) * Math.min(Math.abs(average), limit);
    }
  }
  return m;
}

/** `M … C …` through every point, in order. Two points give a straight line. */
export function monotonePath(points: Point[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${round(points[0].x)} ${round(points[0].y)}`;

  const m = tangents(points);
  let d = `M ${round(points[0].x)} ${round(points[0].y)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const third = (p1.x - p0.x) / 3;
    d +=
      ` C ${round(p0.x + third)} ${round(p0.y + m[i] * third)}` +
      ` ${round(p1.x - third)} ${round(p1.y - m[i + 1] * third)}` +
      ` ${round(p1.x)} ${round(p1.y)}`;
  }
  return d;
}

/** The same curve, closed down to `baseY` — the shape under the line. */
export function monotoneAreaPath(points: Point[], baseY: number): string {
  if (points.length < 2) return "";
  const first = points[0];
  const last = points[points.length - 1];
  return `${monotonePath(points)} L ${round(last.x)} ${round(baseY)} L ${round(first.x)} ${round(
    baseY
  )} Z`;
}

/**
 * Maps a series of values onto a box, oldest at the left.
 *
 * The y range is padded rather than fitted tight to min/max: a balance that
 * has only ever moved a few hundred kroner would otherwise be drawn as a
 * mountain range, every wobble amplified to the full height of the box. The
 * padding is a share of the *span*, so it scales with how eventful the
 * ledger actually is, and a completely flat ledger (span zero) is centred
 * instead of dividing by nothing.
 */
export function projectToBox(
  values: number[],
  width: number,
  height: number,
  { padTop = 0.18, padBottom = 0.12 }: { padTop?: number; padBottom?: number } = {}
): Point[] {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const top = span === 0 ? min + 1 : max + span * padTop;
  const bottom = span === 0 ? min - 1 : min - span * padBottom;
  const range = top - bottom;

  return values.map((v, i) => ({
    x: values.length === 1 ? width / 2 : (i / (values.length - 1)) * width,
    y: height - ((v - bottom) / range) * height,
  }));
}
